import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import './App.css';

// 域名重定向 - 将 www.entalk.cn 重定向到 entalk.cn
const DomainRedirect = ({ children }) => {
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hostname === 'www.entalk.cn') {
      const newUrl = window.location.href.replace('www.entalk.cn', 'entalk.cn');
      window.location.replace(newUrl);
    }
  }, []);
  
  return children;
};

const LoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>加载中...</div>
);

const Home = lazy(() => import('./pages/Home'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const AccountActivation = lazy(() => import('./pages/AccountActivation'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const RegisterWithInvitation = lazy(() => import('./pages/RegisterWithInvitation'));
const Courses = lazy(() => import('./pages/Courses'));
const CourseDetail = lazy(() => import('./pages/CourseDetail'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Profile = lazy(() => import('./pages/Profile'));
const Lesson = lazy(() => import('./pages/Lesson'));
const LearningHistory = lazy(() => import('./pages/LearningHistory'));
const TestSubtitle = lazy(() => import('./pages/TestSubtitle'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminUpload = lazy(() => import('./pages/AdminUpload'));
const AdminManage = lazy(() => import('./pages/AdminManage'));
const AdminVocabulary = lazy(() => import('./pages/AdminVocabulary'));
const AdminInvitation = lazy(() => import('./pages/AdminInvitation'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const EnglishCards = lazy(() => import('./pages/EnglishCards'));
const VideoLibrary = lazy(() => import('./pages/VideoLibrary'));
const AudioLibrary = lazy(() => import('./pages/AudioLibrary'));
const LearningStatistics = lazy(() => import('./pages/LearningStatistics'));

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingFallback />;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingFallback />;
  }
  
  if (user) {
    return <Navigate to="/home" replace />;
  }
  
  return children;
};

const AppRoutes = () => {
  const SKIP_ALL_AUTH = false;
  
  return (
    <Suspense fallback={<LoadingFallback />}>
      <main className="main-content" style={{ paddingTop: 0 }}>
        <Routes>
          <Route path="/login" element={SKIP_ALL_AUTH ? <Login /> : <PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={SKIP_ALL_AUTH ? <Register /> : <PublicRoute><Register /></PublicRoute>} />
          <Route path="/register/:code" element={SKIP_ALL_AUTH ? <RegisterWithInvitation /> : <PublicRoute><RegisterWithInvitation /></PublicRoute>} />
          <Route path="/activation" element={SKIP_ALL_AUTH ? <AccountActivation /> : <PublicRoute><AccountActivation /></PublicRoute>} />
          <Route path="/demo-lesson/:courseId/:lessonId" element={<Lesson />} />
          <Route path="/demo-lesson/:id" element={<Lesson />} />

          <Route path="/" element={SKIP_ALL_AUTH ? <LandingPage /> : <PublicRoute><LandingPage /></PublicRoute>} />
          <Route path="/home" element={SKIP_ALL_AUTH ? <Home /> : <ProtectedRoute><Home /></ProtectedRoute>} />
          <Route path="/courses" element={SKIP_ALL_AUTH ? <Courses /> : <ProtectedRoute><Courses /></ProtectedRoute>} />
          <Route path="/courses/:id" element={SKIP_ALL_AUTH ? <CourseDetail /> : <ProtectedRoute><CourseDetail /></ProtectedRoute>} />
          <Route path="/lesson/:courseId/:lessonId" element={SKIP_ALL_AUTH ? <Lesson /> : <ProtectedRoute><Lesson /></ProtectedRoute>} />
          <Route path="/lesson/:id" element={SKIP_ALL_AUTH ? <Lesson /> : <ProtectedRoute><Lesson /></ProtectedRoute>} />
          <Route path="/learning-history" element={SKIP_ALL_AUTH ? <LearningHistory /> : <ProtectedRoute><LearningHistory /></ProtectedRoute>} />
          <Route path="/video-library" element={SKIP_ALL_AUTH ? <VideoLibrary /> : <ProtectedRoute><VideoLibrary /></ProtectedRoute>} />
          <Route path="/audio-library" element={SKIP_ALL_AUTH ? <AudioLibrary /> : <ProtectedRoute><AudioLibrary /></ProtectedRoute>} />
          <Route path="/learning-statistics" element={SKIP_ALL_AUTH ? <LearningStatistics /> : <ProtectedRoute><LearningStatistics /></ProtectedRoute>} />
          <Route path="/dashboard" element={SKIP_ALL_AUTH ? <Dashboard /> : <ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/profile" element={SKIP_ALL_AUTH ? <Profile /> : <ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/test-subtitle" element={SKIP_ALL_AUTH ? <TestSubtitle /> : <ProtectedRoute><TestSubtitle /></ProtectedRoute>} />
          <Route path="/english-cards" element={SKIP_ALL_AUTH ? <EnglishCards /> : <ProtectedRoute><EnglishCards /></ProtectedRoute>} />
          
          <Route path="/admin" element={<Navigate to="/admin/login" />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/upload" element={<AdminUpload />} />
          <Route path="/admin/manage" element={<AdminManage />} />
          <Route path="/admin/vocabulary" element={<AdminVocabulary />} />
          <Route path="/admin/vocabulary/:lessonId" element={<AdminVocabulary />} />
          <Route path="/admin/invitation" element={<AdminInvitation />} />
          <Route path="/admin/users" element={<AdminUsers />} />
        </Routes>
      </main>
    </Suspense>
  );
};

function App() {
  return (
    <DomainRedirect>
      <AuthProvider>
        <Router future={{ v7_relativeSplatPath: true }}>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </DomainRedirect>
  );
}

export default App;
